// =============================================================================
// src/features/analytics/analytics-live-store.ts - Override vivo do Analytics.
//
// Padrao reputation-live-store: dados reais (AtalaIA social_listening via
// Hasura, source sl_*) entram como override; sem patch, os getters devolvem o
// mock estatico intacto. Patch parcial: campo ausente = mock fica (convencao
// sentinel-overlay). Arrays vazios NAO sobrescrevem (live degrada silencioso).
// SRP: so estado de override + getters. Sem DOM, sem fetch.
// =============================================================================

import {
  OMAR_ANALYTICS_CHART_SERIES,
  OMAR_ANALYTICS_COMPARISON_METRICS,
  OMAR_ANALYTICS_FEED_ITEMS,
  OMAR_ANALYTICS_INFLUENCERS,
  OMAR_ANALYTICS_KPIS,
  OMAR_ANALYTICS_MONTH_TOTALS,
  OMAR_ANALYTICS_PERIOD_LABEL,
  OMAR_ANALYTICS_PLATFORM_COUNTS,
  OMAR_ANALYTICS_SITES,
  type OmarAnalyticsChartSeries,
  type OmarAnalyticsComparisonMetric,
  type OmarAnalyticsInfluencer,
  type OmarAnalyticsIngestionConfig,
  type OmarAnalyticsKpi,
  type OmarAnalyticsMention,
  type OmarAnalyticsPlatformCount,
  type OmarAnalyticsSite,
  type OmarAnalyticsWindowSlice,
} from './analytics-data';

/** Totais do lede (h1 do modulo Analise + card "Resumo de IA" do feed). */
export interface AnalyticsLedeTotals {
  readonly mentionsLabel: string;
  readonly periodLabel: string;
}

/** Total REAL de mencoes de um personagem na janela (chips do feed). */
export interface AnalyticsActorTotal {
  readonly actorSlug: string;
  readonly actorName: string;
  readonly totalMentions: number;
  readonly mentionsByPlatformLabel: Record<string, number>;
}

/** 025: frescor da ingestao (eyebrow "atualizado ha X" + aviso de coleta parada). */
export interface AnalyticsFreshness {
  readonly lastCollectedAtIso: string;
  readonly ageMinutes: number;
  /** true quando a ultima coleta passou de 6h (ANALYTICS_FRESHNESS_STALE_MINUTES). */
  readonly isStale: boolean;
  /** "ha 12 min" · "ha 3 h" · "ha 2 dias". */
  readonly label: string;
  readonly platformsCollecting24h: number;
}

/** Patch parcial da hidratacao ao vivo — campo ausente mantem o mock. */
export interface AnalyticsLivePatch {
  /** 025: SO no live; null no mock (o eyebrow diz "base demonstrativa"). */
  readonly freshness?: AnalyticsFreshness;
  readonly feedItems?: readonly OmarAnalyticsMention[];
  readonly chartSeries?: readonly OmarAnalyticsChartSeries[];
  readonly kpis?: readonly OmarAnalyticsKpi[];
  readonly platformCounts?: readonly OmarAnalyticsPlatformCount[];
  readonly influencers?: readonly OmarAnalyticsInfluencer[];
  readonly sites?: readonly OmarAnalyticsSite[];
  readonly comparisonMetrics?: readonly OmarAnalyticsComparisonMetric[];
  readonly ledeTotals?: AnalyticsLedeTotals;
  readonly actorTotals?: readonly AnalyticsActorTotal[];
  /** 021: recortes 30d da aba Análises (temas/regiões) — SO no live. */
  readonly themeWindow?: readonly OmarAnalyticsWindowSlice[];
  readonly regionWindow?: readonly OmarAnalyticsWindowSlice[];
  /** 021: espelho da configuração real de ingestão (aba Configurações). */
  readonly ingestionConfig?: OmarAnalyticsIngestionConfig;
}

const STATIC_LEDE_TOTALS: AnalyticsLedeTotals = {
  mentionsLabel: OMAR_ANALYTICS_MONTH_TOTALS.mentionsLabel,
  periodLabel: OMAR_ANALYTICS_PERIOD_LABEL,
};

let analyticsLiveOverride: AnalyticsLivePatch = {};
let analyticsEmptiedLiveFields: readonly string[] = [];

/** Feed de mencoes (override vivo ou mock). */
export function getAnalyticsFeedItems(): readonly OmarAnalyticsMention[] {
  return analyticsLiveOverride.feedItems ?? OMAR_ANALYTICS_FEED_ITEMS;
}

/** Series do chart por granularidade (override vivo ou mock). */
export function getAnalyticsChartSeries(): readonly OmarAnalyticsChartSeries[] {
  return analyticsLiveOverride.chartSeries ?? OMAR_ANALYTICS_CHART_SERIES;
}

/** Strip de KPIs do modulo Analise (override vivo ou mock). */
export function getAnalyticsKpis(): readonly OmarAnalyticsKpi[] {
  return analyticsLiveOverride.kpis ?? OMAR_ANALYTICS_KPIS;
}

/** Contagens por canal do filter rail (override vivo ou mock). */
export function getAnalyticsPlatformCounts(): readonly OmarAnalyticsPlatformCount[] {
  return analyticsLiveOverride.platformCounts ?? OMAR_ANALYTICS_PLATFORM_COUNTS;
}

/** Tabela de influenciadores (override vivo ou mock). */
export function getAnalyticsInfluencers(): readonly OmarAnalyticsInfluencer[] {
  return analyticsLiveOverride.influencers ?? OMAR_ANALYTICS_INFLUENCERS;
}

/** Tabela de sites/veiculos (override vivo ou mock). */
export function getAnalyticsSites(): readonly OmarAnalyticsSite[] {
  return analyticsLiveOverride.sites ?? OMAR_ANALYTICS_SITES;
}

/** Cards de comparacao de periodos (override vivo ou mock). */
export function getAnalyticsComparisonMetrics(): readonly OmarAnalyticsComparisonMetric[] {
  return analyticsLiveOverride.comparisonMetrics ?? OMAR_ANALYTICS_COMPARISON_METRICS;
}

/** Totais do lede/h1 (override vivo ou os totais mensais do mock). */
export function getAnalyticsLedeTotals(): AnalyticsLedeTotals {
  return analyticsLiveOverride.ledeTotals ?? STATIC_LEDE_TOTALS;
}

/** Totais por personagem (SO no live; mock nao tem chips de ator). */
export function getAnalyticsActorTotals(): readonly AnalyticsActorTotal[] {
  return analyticsLiveOverride.actorTotals ?? [];
}

/** Temas na janela 30d (SO no live; mock nao tem recortes reais). */
export function getAnalyticsThemeWindow(): readonly OmarAnalyticsWindowSlice[] {
  return analyticsLiveOverride.themeWindow ?? [];
}

/** Regiões citadas na janela 30d (SO no live). */
export function getAnalyticsRegionWindow(): readonly OmarAnalyticsWindowSlice[] {
  return analyticsLiveOverride.regionWindow ?? [];
}

/** Configuração REAL de ingestão (SO no live; null mantém o mock da aba). */
export function getAnalyticsIngestionConfig(): OmarAnalyticsIngestionConfig | null {
  return analyticsLiveOverride.ingestionConfig ?? null;
}

/** Frescor da ingestao (SO no live). */
export function getAnalyticsFreshness(): AnalyticsFreshness | null {
  return analyticsLiveOverride.freshness ?? null;
}

/** 'live' quando algum campo foi patcheado pela hidratacao; senao 'static'. */
export function analyticsLiveSource(): 'live' | 'static' {
  return Object.keys(analyticsLiveOverride).length > 0 ? 'live' : 'static';
}

/** Aplica o patch vivo; arrays vazios sao descartados (mock permanece) MAS ficam
 * registrados: com o filtro de escopo (017/018) um recorte pode legitimamente
 * zerar, e cair no mock em silencio seria mentir para o operador. */
export function applyAnalyticsLivePatch(patch: AnalyticsLivePatch): void {
  const accepted: Record<string, unknown> = {};
  const emptied: string[] = [];
  for (const [patchField, patchValue] of Object.entries(patch)) {
    if (patchValue == null) continue;
    if (Array.isArray(patchValue) && patchValue.length === 0) {
      emptied.push(patchField);
      continue;
    }
    accepted[patchField] = patchValue;
  }
  analyticsLiveOverride = { ...analyticsLiveOverride, ...accepted };
  analyticsEmptiedLiveFields = [...analyticsEmptiedLiveFields, ...emptied];
}

/** Troca de escopo/periodo (2026-09-02): o override anterior e DESCARTADO antes
 * de aplicar o novo — o merge de applyAnalyticsLivePatch deixaria o feed do
 * escopo antigo no ar quando o novo recorte vem vazio. */
export function replaceAnalyticsLivePatch(patch: AnalyticsLivePatch): void {
  analyticsLiveOverride = {};
  analyticsEmptiedLiveFields = [];
  applyAnalyticsLivePatch(patch);
}

/** Campos que voltaram VAZIOS do live e por isso seguem exibindo o mock. */
export function analyticsEmptyLiveFields(): readonly string[] {
  return analyticsEmptiedLiveFields;
}

export function __resetAnalyticsLiveForTests(): void {
  analyticsLiveOverride = {};
  analyticsEmptiedLiveFields = [];
}
